import React, { useState, useEffect, useRef } from 'react';
import { Play, Check, Loader2, Copy, ExternalLink, AlertCircle, Video, Info, Wand2, Edit3, Save, Plus, Trash2, Share2, Users, Store, X, Globe, LogOut } from 'lucide-react';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { ShareModal } from '../ui/ShareModal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { apiService } from '../../services/apiService';
import { auth } from '../../firebase';
import { ListingResult, ListingContext } from '../../types';
import { TOOL_COSTS } from '../../constants';
import { useToast } from '../ui/Toast';

interface ListingOptimizerProps {
  onCostIncurred: (cost: number) => Promise<void>;
  checkCredits: (cost: number) => boolean;
  onRefund: (amount: number, reason: string) => Promise<void>; // Added refund prop
}

const LANGUAGES = [
  { code: 'en', name: 'English (Default)' },
  { code: 'vi', name: 'Tiếng Việt (Vietnamese)' },
  { code: 'es', name: 'Español (Spanish)' },
  { code: 'fr', name: 'Français (French)' },
  { code: 'de', name: 'Deutsch (German)' },
  { code: 'it', name: 'Italiano (Italian)' },
  { code: 'pt', name: 'Português (Portuguese)' },
  { code: 'nl', name: 'Nederlands (Dutch)' },
  { code: 'pl', name: 'Polski (Polish)' },
  { code: 'tr', name: 'Türkçe (Turkish)' },
  { code: 'zh', name: '中文 (Chinese)' },
  { code: 'ja', name: '日本語 (Japanese)' },
  { code: 'ko', name: '한국어 (Korean)' },
  { code: 'ru', name: 'Русский (Russian)' },
  { code: 'ar', name: 'العربية (Arabic)' },
  { code: 'hi', name: 'हिन्दी (Hindi)' },
  { code: 'th', name: 'ไทย (Thai)' },
  { code: 'id', name: 'Bahasa Indonesia' }
];

const ResultField = ({ label, content, onCopy }: { label: string, content: string, onCopy: () => void }) => (
  <div className="space-y-1">
    <div className="flex justify-between items-center">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <button onClick={onCopy} className="text-slate-400 hover:text-primary-600 transition-colors" title="Copy">
        <Copy size={14} />
      </button>
    </div>
    <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-800 font-medium border border-slate-200 break-words">
      {content}
    </div>
  </div>
);

const RiskBadge = ({ label, content }: { label: string, content: string }) => {
  const isSafe = !content || content.toLowerCase().includes('none') || content.toLowerCase().includes('safe') || content.toLowerCase().includes('low') || content.toLowerCase().includes('no risk');
  return (
    <div className={`p-3 rounded-lg border ${isSafe ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-amber-50 border-amber-100 text-amber-800'}`}>
      <span className="text-xs font-bold uppercase block mb-1 opacity-80">{label}</span>
      <p className="text-sm font-medium">{content || 'None detected'}</p>
    </div>
  );
};

export const ListingOptimizer: React.FC<ListingOptimizerProps> = ({ onCostIncurred, checkCredits, onRefund }) => {
  const { addToast } = useToast();
  // Input State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [autoRunVideo, setAutoRunVideo] = useState(false);
  const [outputLanguage, setOutputLanguage] = useState<string>('en');
  
  // Context Library State
  const [contexts, setContexts] = useState<ListingContext[]>([]);
  const [selectedContextIds, setSelectedContextIds] = useState<Set<string>>(new Set());
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [showAddContext, setShowAddContext] = useState(false);
  const [newContextName, setNewContextName] = useState('');
  const [newContextContent, setNewContextContent] = useState('');
  
  // Share State
  const [shareContextId, setShareContextId] = useState<string | null>(null);

  // Confirm Modal State
  const [confirmConfig, setConfirmConfig] = useState<{isOpen: boolean; title: string; description: string; onConfirm: () => Promise<void>; isDestructive: boolean;}>({
      isOpen: false, title: '', description: '', onConfirm: async () => {}, isDestructive: false
  });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // Image handling
  const [inputImage, setInputImage] = useState<string | null>(null); // Base64
  const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);

  // Process State
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<ListingResult['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Video State
  const [editableVideoPrompt, setEditableVideoPrompt] = useState('');
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [isVideoStarting, setIsVideoStarting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [videoStatus, setVideoStatus] = useState<string>('idle');
  const [videoLink, setVideoLink] = useState<string | null>(null);
  const [pollingLogs, setPollingLogs] = useState<string[]>([]);
  
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchContexts();
  }, []);

  const fetchContexts = async () => {
    try {
      setIsContextLoading(true);
      const res = await apiService.getListingContexts();
      if (res.success) setContexts(res.contexts);
    } catch (e) {
      console.error("Failed to fetch contexts", e);
    } finally {
      setIsContextLoading(false);
    }
  };

  const handleCreateContext = async () => {
    if (!newContextName || !newContextContent) return;
    try {
      const res = await apiService.saveListingContext(newContextName, newContextContent);
      if (res.success) {
        addToast("Context saved successfully!", 'success');
        setNewContextName('');
        setNewContextContent('');
        setShowAddContext(false);
        fetchContexts();
      }
    } catch (e) { addToast("Failed to save context", 'error'); }
  };

  const handleDeleteContext = (context: ListingContext, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const isSharedWithMe = context.isSharedWithMe;
    
    setConfirmConfig({
        isOpen: true,
        title: isSharedWithMe ? "Remove Shared Context" : "Delete Context",
        description: isSharedWithMe 
            ? "Remove this shared context from your library?" 
            : "Permanently delete this branding context? This cannot be undone.",
        isDestructive: true,
        onConfirm: async () => {
            setIsConfirmLoading(true);
            try {
              if (isSharedWithMe) {
                  const myEmail = auth.currentUser?.email;
                  if (myEmail) {
                      await apiService.revokeShareListingContext(context.id, myEmail);
                      addToast("Removed shared context", 'info');
                  }
              } else {
                  await apiService.deleteListingContext(context.id);
                  addToast("Context deleted", 'info');
              }
              
              setContexts(prev => prev.filter(c => c.id !== context.id));
              setSelectedContextIds(prev => {
                const next = new Set(prev);
                next.delete(context.id);
                return next;
              });
              setConfirmConfig(prev => ({...prev, isOpen: false}));
            } catch (e: any) { addToast("Action failed: " + e.message, 'error'); }
            finally { setIsConfirmLoading(false); }
        }
    });
  };

  const handleShareClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setShareContextId(id);
  };

  const handleShareConfirm = async (email: string) => {
    if (!shareContextId) return;
    try {
      await apiService.shareListingContext(shareContextId, email);
      addToast(`Shared context with ${email}`, 'success');
      setContexts(prev => prev.map(c => {
          if (c.id === shareContextId) {
              const newShared = c.sharedWith ? [...c.sharedWith, email] : [email];
              return { ...c, sharedWith: newShared };
          }
          return c;
      }));
    } catch (e: any) {
      addToast("Failed to share context: " + e.message, 'error');
      throw e; 
    }
  };

  const handleRevokeShare = async (email: string) => {
      if (!shareContextId) return;
      try {
          await apiService.revokeShareListingContext(shareContextId, email);
          addToast(`Removed access for ${email}`, 'success');
          setContexts(prev => prev.map(c => {
              if (c.id === shareContextId) {
                  const newShared = c.sharedWith?.filter(e => e !== email) || [];
                  return { ...c, sharedWith: newShared };
              }
              return c;
          }));
      } catch (e: any) {
          addToast("Failed to revoke: " + e.message, 'error');
          throw e;
      }
  };

  const toggleContextSelection = (id: string) => {
    setSelectedContextIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setInputImage(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    const totalCost = autoRunVideo ? (TOOL_COSTS.listing + TOOL_COSTS.video_generation) : TOOL_COSTS.listing;
    if (!checkCredits(totalCost)) return;

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    setJobId(null);
    setVideoLink(null);
    setVideoStatus('idle');
    setPollingLogs([]);
    setEditableVideoPrompt('');

    const selectedContexts = contexts.filter(c => selectedContextIds.has(c.id));
    const combinedContext = selectedContexts.map(c => `${c.name}: ${c.content}`).join('\n\n');

    try {
      const skipHistory = autoRunVideo;
      
      const result = await apiService.optimizeListing({
        title,
        description,
        tags,
        imageData: inputImage || undefined,
        skipHistory,
        additionalContext: combinedContext || undefined,
        language: outputLanguage
      });

      if (result.success && result.data) {
        await onCostIncurred(TOOL_COSTS.listing);
        setAnalysisResult(result.data);
        if (result.data.imageUrl) setProcessedImageUrl(result.data.imageUrl);

        let promptStr = result.data.videoPrompt;
        try {
            const jsonObj = JSON.parse(promptStr);
            promptStr = JSON.stringify(jsonObj, null, 2);
        } catch (e) { }
        setEditableVideoPrompt(promptStr);

        if (autoRunVideo) {
            await startVideoProcess(result.data.imageUrl, promptStr, {
                title: result.data.newTitle,
                description: result.data.newDescription,
                tags: result.data.newTags,
                seoSuggestions: result.data.seoSuggestions,
                policyRisk: result.data.policyRisk,
                ipRisk: result.data.ipRisk
            });
        }

      } else {
        setError(result.error || "Analysis failed");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const startVideoProcess = async (imgUrl: string | undefined, prompt: string, listingData?: any) => {
      if (!autoRunVideo) {
          if (!checkCredits(TOOL_COSTS.video_generation)) return;
      }

      const finalImageUrl = imgUrl || processedImageUrl || inputImage;
      if (!finalImageUrl) {
          setError("Error: No valid image URL available for video.");
          return;
      }

      setIsVideoStarting(true);
      setPollingLogs(prev => [...prev, "Initiating video generation..."]);

      try {
        const result = await apiService.startVideoGeneration({
          referenceName: (listingData && listingData.title) ? listingData.title : (title || "Untitled Listing"),
          videoPrompt: prompt,
          imageUrl: finalImageUrl,
          listingData
        });

        if (result.success && result.jobId) {
          await onCostIncurred(TOOL_COSTS.video_generation);
          setJobId(result.jobId);
          setVideoStatus('polling');
          setPollingLogs(prev => [...prev, `Job started: ${result.jobId}`]);
        } else {
          setError("Failed to start video job: " + (result.error || "Unknown error"));
          setVideoStatus('failed');
        }
      } catch (err: any) {
        setError("Error starting video: " + err.message);
        setVideoStatus('failed');
      } finally {
        setIsVideoStarting(false);
      }
  };

  const handleManualVideoStart = () => {
      const listingPayload = analysisResult ? {
          title: analysisResult.newTitle,
          description: analysisResult.newDescription,
          tags: analysisResult.newTags,
          seoSuggestions: analysisResult.seoSuggestions,
          policyRisk: analysisResult.policyRisk,
          ipRisk: analysisResult.ipRisk
      } : undefined;

      startVideoProcess(processedImageUrl || undefined, editableVideoPrompt, listingPayload);
  };

  useEffect(() => {
    if (videoStatus === 'polling' && jobId) {
      const poll = async () => {
        try {
          const statusResult = await apiService.checkVideoStatus(jobId);
          if (statusResult.status === 'done') {
             setVideoStatus('completed');
             setVideoLink(statusResult.videoLink || null);
             setPollingLogs(prev => [...prev, "Video generation completed successfully!"]);
             addToast("Video generation completed!", 'success');
          } else if (statusResult.status === 'error' || statusResult.status === 'cancelled') {
             setVideoStatus('failed');
             const errMsg = `Video generation ${statusResult.status}: ${statusResult.error || 'Unknown reason'}`;
             setError(errMsg);
             setPollingLogs(prev => [...prev, `Failed: ${statusResult.status}`]);
             
             // NEW: Refund logic if failure occurs after charging
             await onRefund(TOOL_COSTS.video_generation, `Failed Video Job (${statusResult.error || 'unknown'})`);
          } else {
             setPollingLogs(prev => {
                const last = prev[prev.length - 1];
                const msg = `Status: ${statusResult.status || 'processing'}...`;
                if (last && last.startsWith("Status:")) return [...prev.slice(0, -1), msg];
                return [...prev, msg];
             });
             pollTimeoutRef.current = setTimeout(poll, 10000);
          }
        } catch (err) {
           console.error("Polling error", err);
           pollTimeoutRef.current = setTimeout(poll, 10000);
        }
      };
      pollTimeoutRef.current = setTimeout(poll, 5000);
    }
    return () => { if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current); };
  }, [videoStatus, jobId, addToast, onRefund]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast("Copied to clipboard", 'info');
  };

  const activeContexts = contexts.filter(c => selectedContextIds.has(c.id));
  const availableContexts = contexts.filter(c => !selectedContextIds.has(c.id));

  return (
    <div className="space-y-8">
      {/* Modals */}
      <ShareModal 
        isOpen={!!shareContextId}
        onClose={() => setShareContextId(null)}
        onConfirm={handleShareConfirm}
        onRevoke={handleRevokeShare}
        currentSharedWith={contexts.find(c => c.id === shareContextId)?.sharedWith}
        title="Share Brand Context"
        description="Share this context configuration with another team member."
      />
      
      <ConfirmModal 
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig(prev => ({...prev, isOpen: false}))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        description={confirmConfig.description}
        isDestructive={confirmConfig.isDestructive}
        isLoading={isConfirmLoading}
        confirmText="Delete"
      />

      {/* Input Section */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
        {/* ... (Same input fields as previous version) ... */}
        <div className="flex justify-between items-center mb-4">
             <h3 className="text-lg font-semibold text-slate-800">Listing Details</h3>
             <div className="flex items-center gap-2 text-sm">
                 <Globe size={16} className="text-slate-500" />
                 <span className="text-slate-600">Analysis Language:</span>
                 <select 
                    value={outputLanguage} 
                    onChange={(e) => setOutputLanguage(e.target.value)}
                    className="border-slate-300 rounded-md text-sm py-1 pl-2 pr-6 focus:ring-0 focus:border-primary-500 cursor-pointer"
                 >
                     {LANGUAGES.map(lang => (
                       <option key={lang.code} value={lang.code}>{lang.name}</option>
                     ))}
                 </select>
             </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Product Image</label>
            <FileUpload 
                onFileSelect={handleFileSelect}
                previewUrl={inputImage}
                label="Upload Product Image"
                subLabel="JPG or PNG. Used for visual analysis and video generation."
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Title</label>
            <input 
              type="text" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Description</label>
            <textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Current Tags</label>
            <input 
              type="text" 
              value={tags} 
              onChange={(e) => setTags(e.target.value)}
              placeholder="tag1, tag2, tag3"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Context Library Section */}
        <div className="col-span-1 md:col-span-2 border-t border-slate-100 pt-6 mt-2">
           <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-slate-700 flex items-center gap-2">
                 <Store size={16} className="text-primary-600" />
                 Store Context & Branding <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
           </div>

           {/* 1. Applied Contexts */}
           <div className="mb-6">
               <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                   Applied Contexts ({activeContexts.length})
               </h4>
               {activeContexts.length > 0 ? (
                   <div className="flex flex-wrap gap-2">
                       {activeContexts.map(context => (
                           <div key={context.id} className="flex items-center gap-2 bg-primary-50 text-primary-800 px-3 py-1.5 rounded-full border border-primary-200 text-sm shadow-sm">
                               <span className="font-semibold">{context.name}</span>
                               <span className="text-xs opacity-70 border-l border-primary-200 pl-2 ml-1 max-w-[200px] truncate hidden sm:inline">{context.content}</span>
                               <button 
                                   onClick={() => toggleContextSelection(context.id)}
                                   className="ml-1 p-0.5 hover:bg-primary-100 rounded-full text-primary-600 transition-colors"
                               >
                                   <X size={14} />
                               </button>
                           </div>
                       ))}
                   </div>
               ) : (
                   <p className="text-sm text-slate-400 italic bg-slate-50 p-3 rounded-lg border border-slate-100 border-dashed">
                       No contexts applied. Select from the library below to help AI understand your brand.
                   </p>
               )}
           </div>

           {/* 2. Context Library */}
           <div>
               <div className="flex justify-between items-end mb-2">
                   <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Library</h4>
                   <button 
                        onClick={() => setShowAddContext(!showAddContext)} 
                        className="text-xs font-semibold text-primary-600 hover:text-primary-800 flex items-center gap-1"
                    >
                        <Plus size={14} /> Add New
                    </button>
               </div>

               {/* Add Context Form */}
               {showAddContext && (
                   <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4 animate-fade-in">
                       <div className="grid grid-cols-1 gap-3">
                           <input 
                              type="text" 
                              placeholder="Context Name (e.g. 'Eco-Friendly Store')" 
                              value={newContextName}
                              onChange={(e) => setNewContextName(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md"
                           />
                           <textarea 
                              placeholder="Context Content (e.g. 'We sell 100% organic cotton products to environmentally conscious millennials...')"
                              value={newContextContent}
                              onChange={(e) => setNewContextContent(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md h-20"
                           />
                           <div className="flex justify-end gap-2">
                               <Button variant="secondary" onClick={() => setShowAddContext(false)} className="py-1 px-3 text-xs">Cancel</Button>
                               <Button onClick={handleCreateContext} disabled={!newContextName || !newContextContent} className="py-1 px-3 text-xs">Save Context</Button>
                           </div>
                       </div>
                   </div>
               )}

               {isContextLoading ? <div className="text-center py-4"><Loader2 className="animate-spin text-slate-400 mx-auto" size={20} /></div> : (
                   <div className="flex flex-wrap gap-3">
                       {availableContexts.map(context => (
                           <div 
                              key={context.id}
                              onClick={() => toggleContextSelection(context.id)}
                              className="group relative flex flex-col p-3 rounded-lg border border-slate-200 bg-white hover:border-primary-300 hover:bg-slate-50 cursor-pointer transition-all w-full sm:w-[calc(50%-0.5rem)]"
                           >
                               <div className="flex justify-between items-start mb-1">
                                   <span className="font-semibold text-sm text-slate-800">{context.name}</span>
                                   <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                                       <Plus size={12} />
                                   </div>
                               </div>
                               <p className="text-xs text-slate-500 line-clamp-2">{context.content}</p>
                               {context.isSharedWithMe && <div className="mt-2 text-[10px] text-purple-600 font-bold flex items-center gap-1"><Users size={10} /> Shared</div>}
                               
                               {/* Actions */}
                               <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 backdrop-blur-sm rounded-md p-0.5 shadow-sm" onClick={e => e.stopPropagation()}>
                                  {!context.isSharedWithMe && (
                                      <button onClick={(e) => handleShareClick(context.id, e)} className="p-1 text-slate-400 hover:text-primary-600 rounded hover:bg-slate-100" title="Share"><Share2 size={12} /></button>
                                  )}
                                  <button onClick={(e) => handleDeleteContext(context, e)} className="p-1 text-slate-400 hover:text-red-600 rounded hover:bg-slate-100" title={context.isSharedWithMe ? "Remove" : "Delete"}>
                                      {context.isSharedWithMe ? <LogOut size={12} /> : <Trash2 size={12} />}
                                  </button>
                               </div>
                           </div>
                       ))}
                       {availableContexts.length === 0 && !showAddContext && (
                           <p className="text-sm text-slate-400 italic w-full text-center py-4">
                               {contexts.length > 0 ? "All contexts are applied." : "No saved contexts found."}
                           </p>
                       )}
                   </div>
               )}
           </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 items-center mt-6 pt-6 border-t border-slate-100">
             <div className="flex-1">
                <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <input 
                        type="checkbox" 
                        checked={autoRunVideo} 
                        onChange={(e) => setAutoRunVideo(e.target.checked)}
                        className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 mt-0.5"
                    />
                    <div>
                        <span className="block text-sm font-semibold text-slate-800">Auto-Generate Video</span>
                        <span className="block text-xs text-slate-500 mb-1">Run optimization + video in one go ({TOOL_COSTS.listing + TOOL_COSTS.video_generation} credits)</span>
                        <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded inline-block border border-amber-100">
                            <strong>Note:</strong> Generates a <strong>Square (1:1)</strong> video. Non-square images will be center-cropped.
                        </p>
                    </div>
                </label>
             </div>
             
             <div className="flex-1 w-full">
                <Button 
                    onClick={handleAnalyze} 
                    isLoading={isAnalyzing}
                    disabled={!inputImage || !title}
                    className="w-full"
                >
                    <div className="flex items-center gap-2">
                        {autoRunVideo ? (
                            <>
                                <Play size={18} /> Run All <span className="text-xs opacity-70 bg-black/20 px-2 py-0.5 rounded-full">{TOOL_COSTS.listing + TOOL_COSTS.video_generation} Credits</span>
                            </>
                        ) : (
                            <>
                                <Wand2 size={18} /> Analyze Listing <span className="text-xs opacity-70 bg-black/20 px-2 py-0.5 rounded-full">{TOOL_COSTS.listing} Credit</span>
                            </>
                        )}
                    </div>
                </Button>
             </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysisResult && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Text Optimization Results */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-lg font-bold text-slate-800">Optimized Content</h3>
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">AI Generated</span>
            </div>

            <ResultField label="New Title" content={analysisResult.newTitle} onCopy={() => copyToClipboard(analysisResult.newTitle)} />
            <ResultField label="New Tags" content={analysisResult.newTags} onCopy={() => copyToClipboard(analysisResult.newTags)} />
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-semibold text-slate-700">New Description</span>
                <button onClick={() => copyToClipboard(analysisResult.newDescription)} className="text-slate-400 hover:text-primary-600"><Copy size={14} /></button>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 whitespace-pre-wrap h-40 overflow-y-auto border border-slate-200">
                {analysisResult.newDescription}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
              <RiskBadge label="Policy Risk" content={analysisResult.policyRisk} />
              <RiskBadge label="IP Risk" content={analysisResult.ipRisk} />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
               <p className="text-xs font-bold text-blue-800 mb-1">SEO Suggestions</p>
               <p className="text-xs text-blue-700">{analysisResult.seoSuggestions}</p>
            </div>
          </div>

          {/* Video Generation Section */}
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Video size={20} className="text-primary-600" /> Video AI
              </h3>
            </div>

            <div className="flex-1 flex flex-col">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-slate-700">Video Prompt</span>
                    <button 
                        onClick={() => setIsEditingPrompt(!isEditingPrompt)} 
                        className="text-primary-600 text-xs font-medium flex items-center gap-1 hover:text-primary-800"
                    >
                        {isEditingPrompt ? <Save size={12} /> : <Edit3 size={12} />}
                        {isEditingPrompt ? 'Save' : 'Edit'}
                    </button>
                </div>
                
                {isEditingPrompt ? (
                    <textarea 
                        value={editableVideoPrompt}
                        onChange={(e) => setEditableVideoPrompt(e.target.value)}
                        className="w-full h-48 p-3 text-xs font-mono border border-primary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                    />
                ) : (
                    <div className="p-3 bg-slate-50 rounded-lg text-xs font-mono text-slate-600 border border-slate-200 h-48 overflow-y-auto whitespace-pre-wrap">
                        {editableVideoPrompt}
                    </div>
                )}
                
                <p className="text-xs text-slate-400 mt-2">
                    {isEditingPrompt ? "Modify the JSON prompt to customize generation." : "Review the generated prompt before starting video."}
                </p>
              </div>

              {videoLink ? (
                 <div className="mt-auto bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
                    <div className="mx-auto bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center text-emerald-600 mb-3">
                      <Check size={24} />
                    </div>
                    <h4 className="font-bold text-emerald-900 mb-1">Video Generated!</h4>
                    <p className="text-sm text-emerald-700 mb-4">Your promotional video is ready.</p>
                    <a 
                      href={videoLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                    >
                       <ExternalLink size={16} /> View Video
                    </a>
                 </div>
              ) : videoStatus === 'polling' ? (
                 <div className="mt-auto bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className="flex items-center justify-center gap-3 mb-4">
                       <Loader2 className="animate-spin text-primary-600" size={24} />
                       <span className="font-semibold text-slate-700">Generating Video...</span>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto mb-4">
                       {pollingLogs.map((log, i) => (
                         <p key={i} className="text-xs text-slate-500 font-mono">
                           <span className="text-slate-300 mr-2">{new Date().toLocaleTimeString()}</span>
                           {log}
                         </p>
                       ))}
                    </div>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
                       <Info size={16} className="text-blue-500 shrink-0 mt-0.5" />
                       <div className="text-xs text-blue-700">
                          <strong>You can leave this page!</strong>
                          <p>The video is generating in the background. Check History later.</p>
                       </div>
                    </div>
                 </div>
              ) : (
                 <div className="mt-auto border-t border-slate-100 pt-4">
                   <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 text-xs rounded-lg mb-3 border border-amber-100">
                       <Info size={14} className="shrink-0 mt-0.5" />
                       <p>This generates a <strong>Square (1:1)</strong> video. If your image is rectangular, the edges will be trimmed (center-cropped).</p>
                   </div>
                   <Button 
                     onClick={handleManualVideoStart} 
                     isLoading={isVideoStarting}
                     className="w-full"
                     disabled={isEditingPrompt}
                   >
                     <Play size={16} /> Generate Video <span className="text-xs opacity-70 bg-black/20 px-2 py-0.5 rounded-full ml-2">{TOOL_COSTS.video_generation} Credits</span>
                   </Button>
                 </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};